import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import friendshipService from '../services/friendshipService';
import { startDirectMessageSession } from '../utils/directMessageNavigation';

const normalizeUserId = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  const normalized = String(value).trim();
  return normalized || null;
};

export default function useProfileContactActions({
  targetUserId: rawTargetUserId,
  viewerUserId: rawViewerUserId,
  onDirectMessageReady,
  enabled = true,
} = {}) {
  const targetUserId = useMemo(() => normalizeUserId(rawTargetUserId), [rawTargetUserId]);
  const viewerUserId = useMemo(() => normalizeUserId(rawViewerUserId), [rawViewerUserId]);
  const isSelfProfile = Boolean(targetUserId && viewerUserId && targetUserId === viewerUserId);
  const [friendshipRelationship, setFriendshipRelationship] = useState('none');
  const [isFriendshipLoading, setIsFriendshipLoading] = useState(false);
  const [isConnectInFlight, setIsConnectInFlight] = useState(false);
  const [isMessageInFlight, setIsMessageInFlight] = useState(false);
  const relationshipRequestRef = useRef(0);
  const mountedRef = useRef(true);
  const currentIdentityRef = useRef({ targetUserId, viewerUserId });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      relationshipRequestRef.current += 1;
    };
  }, []);

  useEffect(() => {
    currentIdentityRef.current = { targetUserId, viewerUserId };
    setIsConnectInFlight(false);
    setIsMessageInFlight(false);
  }, [targetUserId, viewerUserId]);

  useEffect(() => {
    const requestId = relationshipRequestRef.current + 1;
    relationshipRequestRef.current = requestId;

    if (!enabled || !targetUserId || !viewerUserId) {
      setFriendshipRelationship('none');
      setIsFriendshipLoading(false);
      return undefined;
    }

    if (isSelfProfile) {
      setFriendshipRelationship('self');
      setIsFriendshipLoading(false);
      return undefined;
    }

    let cancelled = false;
    setFriendshipRelationship('none');
    setIsFriendshipLoading(true);

    Promise.all([
      friendshipService.getAcceptedFriends(),
      friendshipService.getIncomingRequests(),
      friendshipService.getOutgoingRequests(),
    ])
      .then(([acceptedFriends, incomingRequests, outgoingRequests]) => {
        if (cancelled || relationshipRequestRef.current !== requestId) {
          return;
        }

        const isConnected = (Array.isArray(acceptedFriends) ? acceptedFriends : [])
          .some((friend) => String(friend?.userId ?? friend?.id) === targetUserId);
        if (isConnected) {
          setFriendshipRelationship('friend');
          return;
        }

        const hasIncoming = (Array.isArray(incomingRequests) ? incomingRequests : [])
          .some((request) => String(request?.requesterId) === targetUserId);
        if (hasIncoming) {
          setFriendshipRelationship('incoming');
          return;
        }

        const hasOutgoing = (Array.isArray(outgoingRequests) ? outgoingRequests : [])
          .some((request) => String(request?.addresseeId) === targetUserId);
        if (hasOutgoing) {
          setFriendshipRelationship('outgoing');
          return;
        }

        setFriendshipRelationship('none');
      })
      .catch((error) => {
        console.warn('Failed to resolve friendship relationship for profile actions.', error);
        if (!cancelled && relationshipRequestRef.current === requestId) {
          setFriendshipRelationship('none');
        }
      })
      .finally(() => {
        if (!cancelled && relationshipRequestRef.current === requestId) {
          setIsFriendshipLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, isSelfProfile, targetUserId, viewerUserId]);

  const isConnectDisabled = !enabled
    || isSelfProfile
    || !targetUserId
    || !viewerUserId
    || isFriendshipLoading
    || isConnectInFlight
    || friendshipRelationship !== 'none';

  const isMessageDisabled = !enabled
    || isSelfProfile
    || !targetUserId
    || !viewerUserId
    || isMessageInFlight;

  const handleConnectClick = useCallback(async () => {
    if (isConnectDisabled || !targetUserId) {
      return;
    }

    setIsConnectInFlight(true);
    try {
      await friendshipService.sendFriendRequest(targetUserId);
      if (
        mountedRef.current
        && currentIdentityRef.current.targetUserId === targetUserId
        && currentIdentityRef.current.viewerUserId === viewerUserId
      ) {
        setFriendshipRelationship('outgoing');
      }
    } catch (error) {
      console.error('Failed to send friend request from profile actions.', error);
    } finally {
      if (
        mountedRef.current
        && currentIdentityRef.current.targetUserId === targetUserId
        && currentIdentityRef.current.viewerUserId === viewerUserId
      ) {
        setIsConnectInFlight(false);
      }
    }
  }, [isConnectDisabled, targetUserId, viewerUserId]);

  const handleMessageClick = useCallback(async () => {
    if (isMessageDisabled || !targetUserId) {
      return;
    }

    setIsMessageInFlight(true);
    try {
      await startDirectMessageSession(targetUserId, {
        currentUserId: viewerUserId,
      });
      if (
        currentIdentityRef.current.targetUserId === targetUserId
        && currentIdentityRef.current.viewerUserId === viewerUserId
      ) {
        onDirectMessageReady?.();
      }
    } catch (error) {
      console.error('Failed to open direct message from profile actions.', error);
    } finally {
      if (
        mountedRef.current
        && currentIdentityRef.current.targetUserId === targetUserId
        && currentIdentityRef.current.viewerUserId === viewerUserId
      ) {
        setIsMessageInFlight(false);
      }
    }
  }, [isMessageDisabled, onDirectMessageReady, targetUserId, viewerUserId]);

  return {
    targetUserId,
    viewerUserId,
    isSelfProfile,
    friendshipRelationship,
    isFriendshipLoading,
    isConnectInFlight,
    isMessageInFlight,
    isConnectDisabled,
    isMessageDisabled,
    handleConnectClick,
    handleMessageClick,
  };
}
